import { useEffect, useRef } from 'react';

export default function FeedbackWidget({
  tenantId,
  widgetUrl = "https://feedpanels.com/widget.js",
  primaryColor = "#4b5658",
  backgroundColor = "#ffffff",
  buttonStyle = "rounded-full",
  fontSize = "14px",
  fontFamily = "Inter, sans-serif",
  theme = "light",
  language = "en" // Language selection (tr, en, de, es, fr)
}) {
  // Use refs to track initialization state
  const isInitializedRef = useRef(false);
  const widgetScriptRef = useRef(null);

  // Initialize widget on mount
  useEffect(() => {
    if (!tenantId) {
      console.error('FeedbackWidget: tenantId is required');
      return;
    }

    const containerElement = document.getElementById('feedpanels-widget-container');
    if (!containerElement) {
      console.warn('FeedbackWidget: Container element #feedpanels-widget-container not found. Widget will not be initialized.');
      return;
    }

    const initializeWidget = () => {
      if (window.fbw && !isInitializedRef.current) {
        window.fbw('init', {
          tenantId: tenantId,
          primaryColor: primaryColor,
          backgroundColor: backgroundColor,
          buttonStyle: buttonStyle,
          fontSize: fontSize,
          fontFamily: fontFamily,
          theme: theme,
          language: language,
          container: containerElement
        });
        isInitializedRef.current = true;
      }
    };

    // Only load script if not already loaded
    let script = document.querySelector(`script[src="${widgetUrl}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = widgetUrl;
      script.async = true;
      script.onload = initializeWidget;
      script.onerror = () => {
        console.error('Failed to load FeedbackFlow widget script');
        console.error(`Widget URL: ${widgetUrl}`);
      };
      document.body.appendChild(script);
      widgetScriptRef.current = script;
    } else {
      // Script already loaded, initialize if window.fbw is available
      if (window.fbw) {
        initializeWidget();
      } else {
        // fbw not ready yet, try again after a short delay
        setTimeout(initializeWidget, 100);
      }
    }

    // Since DashboardLayout is now persistent and never unmounts,
    // we don't need to destroy the widget. Just clear ref on unmount.
    return () => {
      // No need to destroy the widget anymore - DashboardLayout is persistent
      // Just clear the initialization flag if component actually unmounts (rare)
      isInitializedRef.current = false;
    };
  }, [tenantId]); // Only re-initialize if tenantId changes

  // Handle language and theme changes - reinitialize widget
  useEffect(() => {
    if (!window.fbw || !isInitializedRef.current) {
      return;
    }

    const containerElement = document.getElementById('feedpanels-widget-container');
    if (!containerElement) {
      return;
    }

    // Reset the widget state by clearing all fbw data
    // This allows us to reinitialize without "already initialized" errors
    if (window.fbw && window.fbw.destroy) {
      try {
        window.fbw.destroy();
      } catch (e) {
        // If destroy fails, continue anyway
        console.warn('Widget destroy warning:', e.message);
      }
    }

    // Clear global fbw reference to reset state
    window.fbw = undefined;

    // Clean up container
    containerElement.innerHTML = '';
    isInitializedRef.current = false;

    // Reload the widget script to reset fbw global
    const existingScript = document.querySelector(`script[src="https://feedpanels.com/widget.js"]`);
    if (existingScript) {
      existingScript.remove();
    }

    // Reload script and reinitialize
    const script = document.createElement('script');
    script.src = 'https://feedpanels.com/widget.js';
    script.async = true;
    script.onload = () => {
      if (window.fbw && containerElement) {
        window.fbw('init', {
          tenantId: tenantId,
          primaryColor: primaryColor,
          backgroundColor: backgroundColor,
          buttonStyle: buttonStyle,
          fontSize: fontSize,
          fontFamily: fontFamily,
          theme: theme,
          language: language,
          container: containerElement
        });
        isInitializedRef.current = true;
      }
    };
    script.onerror = () => {
      console.error('Failed to reload FeedbackFlow widget script');
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts during load
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [language, theme, tenantId, primaryColor, backgroundColor, buttonStyle, fontSize, fontFamily]);

  return null;
}

// ⚙️ Installation Instructions:
// 1. Copy the component code above
// 2. Add the component to an appropriate location in your project (e.g., App.js or Layout)
// 3. Import and render the component: <FeedbackWidget tenantId="03fc92f9-ef6a-49e4-9efe-c97db4e42b57" />
// 4. Save changes and refresh your application
// 5. Check that the widget button appears when the component is rendered
//
// 🌍 Language Support:
// - FeedPanels language: Used by default (optional)
// - Custom language: <FeedbackWidget tenantId="03fc92f9-ef6a-49e4-9efe-c97db4e42b57" language="en" />
// - Supported languages: tr (Turkish), en (English), de (German), es (Spanish), fr (French)
// - Dynamic language change: Widget language updates instantly when language prop changes
// - Your Tenant ID: 03fc92f9-ef6a-49e4-9efe-c97db4e42b57
// Example: const { language } = useLanguage(); <FeedbackWidget tenantId="03fc92f9-ef6a-49e4-9efe-c97db4e42b57" language={language} />